/** Eighth-of-a-page fractions used in scheduling / stripboard (matches common screenplay page-count style). */
const EIGHTH_FRACTION: Record<number, string> = {
  1: "1/8",
  2: "1/4",
  3: "3/8",
  4: "1/2",
  5: "5/8",
  6: "3/4",
  7: "7/8",
};

/**
 * Maps an eighth-of-a-page count (1–96) to the canonical dropdown string (e.g. 12 → "1 1/2").
 * Returns "" when `eighths` is 0 (no content / no length).
 */
export function pageLengthStringFromEighthIndex(eighths: number): string {
  if (eighths <= 0) return "";
  const whole = Math.floor(eighths / 8);
  const remainder = eighths % 8;
  if (remainder === 0) {
    return String(whole);
  }
  const frac = EIGHTH_FRACTION[remainder];
  if (!frac) {
    return String(whole);
  }
  return whole === 0 ? frac : `${whole} ${frac}`;
}

/**
 * Converts total screenplay line count (same basis as editor pagination) into eighths, capped at `maxEighths`.
 */
export function estimatedEighthsFromLineCount(
  totalLines: number,
  linesPerPage: number,
  maxEighths = 96
): number {
  if (totalLines <= 0) return 0;
  const raw = (8 * totalLines) / linesPerPage;
  return Math.max(1, Math.min(maxEighths, Math.round(raw)));
}

/**
 * Page length string for production metadata from a line-total estimate.
 */
export function formatScheduleLengthFromLineTotals(
  totalLines: number,
  linesPerPage: number,
  maxEighths = 96
): string {
  const eighths = estimatedEighthsFromLineCount(totalLines, linesPerPage, maxEighths);
  return pageLengthStringFromEighthIndex(eighths);
}

/**
 * Ordered dropdown values from a small fraction up to `maxEighths` eighths (default 12 pages).
 * First entry is always `""` (clears stored length).
 */
export function buildPageLengthSelectOptions(maxEighths = 96): string[] {
  const ordered: string[] = [""];
  const seen = new Set<string>([""]);

  for (let i = 1; i <= maxEighths; i += 1) {
    const whole = Math.floor(i / 8);
    const remainder = i % 8;
    let value: string;
    if (remainder === 0) {
      if (whole === 0) continue;
      value = String(whole);
    } else {
      const frac = EIGHTH_FRACTION[remainder];
      if (!frac) continue;
      value = whole === 0 ? frac : `${whole} ${frac}`;
    }
    if (!seen.has(value)) {
      seen.add(value);
      ordered.push(value);
    }
  }
  return ordered;
}
